import React, { createContext, useContext, useState, useCallback } from "react";
import AppToast from "../components/AppToast";

const ToastContext = createContext();

export function ToastProvider({ children }) {
  const [toast, setToast] = useState({
    show: false,
    message: "",
    bg: "success",
    title: "",
    actionLabel: "",
    onAction: null,
  });

  const playBeep = async () => {
    // Try playing an MP3 from public/sounds/new-order.mp3 first (exact sound)
    try {
      const audioPath = '/sounds/new-order.mp3';
      const audio = new Audio(audioPath);
      audio.volume = 0.12; // modest volume (0.0 - 1.0)
      // Attempt to play — may be blocked until user interaction
      await audio.play();
      // If play succeeds, return
      return;
    } catch (err) {
      // If file not found or playback blocked, fallback to WebAudio gentle chime
    }

    // Fallback: WebAudio gentle double-chime (soft, WhatsApp-like)
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        ctx.resume().catch(() => {});
      }

      const now = ctx.currentTime;
      const note1 = { freq: 420, dur: 0.09 }; // 90ms
      const gap = 0.08; // 80ms gap
      const note2 = { freq: 620, dur: 0.14 }; // 140ms
      const total = note1.dur + gap + note2.dur + 0.05;

      // Light master gain
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.00001, now);
      masterGain.connect(ctx.destination);

      const playNote = (startTime, frequency, duration) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, startTime);

        // soft envelope
        const attack = 0.01; // 10ms
        const release = Math.min(0.08, duration * 0.6);
        const sustain = Math.max(0, duration - attack - release);

        g.gain.setValueAtTime(0.00001, startTime);
        g.gain.exponentialRampToValueAtTime(0.08, startTime + attack); // low peak
        g.gain.setValueAtTime(0.08, startTime + attack + sustain);
        g.gain.exponentialRampToValueAtTime(0.00001, startTime + attack + sustain + release);

        // per-note filter to keep it smooth
        const noteFilter = ctx.createBiquadFilter();
        noteFilter.type = 'lowpass';
        noteFilter.frequency.setValueAtTime(1200, startTime);
        noteFilter.frequency.linearRampToValueAtTime(900, startTime + duration);

        // slight detuned second oscillator for warmth
        const osc2 = ctx.createOscillator();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(frequency * 0.995, startTime);
        osc2.detune.value = -6;

        osc.connect(noteFilter);
        osc2.connect(noteFilter);
        noteFilter.connect(g);
        g.connect(masterGain);

        osc.start(startTime);
        osc2.start(startTime);
        osc.stop(startTime + duration + 0.02);
        osc2.stop(startTime + duration + 0.02);
      };

      // Play the two notes
      playNote(now, note1.freq, note1.dur);
      playNote(now + note1.dur + gap, note2.freq, note2.dur);

      // Close context when done
      setTimeout(() => {
        try {
          if (typeof ctx.close === 'function') ctx.close();
        } catch (e) {}
      }, (total + 0.2) * 1000);
    } catch (e) {
      // ignore audio errors
    }
  };

  const showToast = useCallback((messageOrOptions, bg = "success", opts = {}) => {
    const payload =
      typeof messageOrOptions === "object" && messageOrOptions !== null
        ? {
            show: true,
            bg: "success",
            title: "",
            actionLabel: "",
            onAction: null,
            playSound: false,
            ...messageOrOptions,
          }
        : {
            show: true,
            message: String(messageOrOptions || ""),
            bg,
            title: "",
            actionLabel: "",
            onAction: null,
            playSound: false,
            ...opts,
          };

    setToast(payload);
    if (payload.playSound) playBeep();
  }, []);

  const hideToast = useCallback(() => {
    setToast((t) => ({ ...t, show: false }));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* 🔥 One global toast */}
      <AppToast
        show={toast.show}
        message={toast.message}
        bg={toast.bg}
        title={toast.title}
        actionLabel={toast.actionLabel}
        onAction={toast.onAction}
        onClose={hideToast}
      />
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
